import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft, Settings, User, Shield, Trash2, AlertTriangle, 
  Clock, Bell, Download, LogOut, ChevronRight, CheckCircle, XCircle,
  CreditCard, ExternalLink, Loader2, Award, Ban, UserX, Baby, Brain,
  Calendar, Sparkles, RefreshCw, Gift
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAIConsent } from '../components/AIConsentDialog';
import { todayLocal, formatLocalDate } from '../utils/dateUtils';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, token, API, logout, refreshUser } = useAuth();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [showBlockedUsers, setShowBlockedUsers] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  
  // Password change state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // AI Consent
  const { hasConsent, resetConsent, setShowConsentDialog } = useAIConsent();
  const [showAIConsentDialog, setShowAIConsentDialog] = useState(false);

  useEffect(() => {
    fetchSubscriptionInfo();
    fetchBlockedUsers();
  }, [token]);

  const fetchBlockedUsers = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/users/blocked`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlockedUsers(response.data.blocked_users || []);
    } catch (error) {
      console.error('Failed to fetch blocked users:', error);
    }
  };

  const handleUnblockUser = async (userId, userName) => {
    try {
      await axios.delete(`${API}/users/block/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${userName} has been unblocked`);
      fetchBlockedUsers();
    } catch (error) {
      toast.error('Failed to unblock user');
    }
  };

  const handleOpenProfileDialog = () => {
    setProfileName(user?.name || '');
    setShowProfileDialog(true);
  };

  const handleSaveProfile = async () => {
    if (!profileName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    setIsSavingProfile(true);
    try {
      await axios.patch(`${API}/auth/me`, 
        { name: profileName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Profile updated successfully!');
      setShowProfileDialog(false);
      // Refresh user data in context
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleOpenPasswordDialog = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowPasswordDialog(true);
  };

  const handleChangePassword = async () => {
    if (!currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    if (!newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    setIsChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, 
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password changed successfully!');
      setShowPasswordDialog(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Password change error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to change password';
      toast.error(errorMessage);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const fetchSubscriptionInfo = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/subscriptions/my-subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription info:', error);
    }
  };

  const handleManageSubscription = async () => {
    setIsLoadingPortal(true);
    try {
      const response = await axios.post(`${API}/subscriptions/portal`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Open Stripe portal in new tab
      window.open(response.data.portal_url, '_blank');
    } catch (error) {
      console.error('Portal error:', error);
      if (error.response?.data?.detail?.includes('No active subscription')) {
        toast.error('No active subscription found. Please subscribe first.');
        navigate('/pricing');
      } else {
        toast.error('Failed to open subscription portal. Please try again.');
      }
    } finally {
      setIsLoadingPortal(false);
    }
  };

  const handleExportData = async () => {
    try {
      setIsExporting(true);
      const response = await axios.get(`${API}/auth/export-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create a blob and download it
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `calm-pathways-data-export-${todayLocal()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Your data has been exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    try {
      setIsDeleting(true);
      await axios.delete(`${API}/auth/delete-account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Your account and all data have been deleted');
      logout();
      navigate('/');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete account. Please contact support.');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const settingsSections = [
    {
      title: 'Account',
      icon: User,
      items: [
        {
          label: 'Profile Information',
          description: `Update your name (${user?.name || 'Not set'})`,
          onClick: handleOpenProfileDialog,
        },
        {
          label: 'Child Profiles',
          description: 'Manage your children\'s profiles',
          onClick: () => navigate('/child-profiles'),
        },
        {
          label: 'Change Password',
          description: 'Update your account password',
          onClick: handleOpenPasswordDialog,
        },
      ]
    },
    {
      title: 'Privacy & Security',
      icon: Shield,
      items: [
        {
          label: 'Session Timeout',
          description: 'Automatic logout after 30 minutes of inactivity',
          info: true,
          infoIcon: CheckCircle,
          infoColor: 'text-green-600',
        },
        {
          label: 'AI Data Sharing',
          description: hasConsent === true ? 'Enabled - AI features active' : hasConsent === false ? 'Disabled - AI features off' : 'Not configured',
          onClick: () => {
            if (hasConsent !== null) {
              // Reset consent so user can rechoose
              resetConsent();
              toast.info('AI consent reset. Visit any AI feature to reconfigure.');
            } else {
              toast.info('Visit any AI feature to configure data sharing.');
            }
          },
          info: true,
          infoIcon: hasConsent === true ? CheckCircle : hasConsent === false ? XCircle : Brain,
          infoColor: hasConsent === true ? 'text-green-600' : hasConsent === false ? 'text-red-600' : 'text-stone-400',
        },
        {
          label: 'Privacy Policy',
          description: 'Read our privacy policy',
          onClick: () => navigate('/privacy-policy'),
        },
        {
          label: 'Terms of Service',
          description: 'Read our terms of service',
          onClick: () => navigate('/terms-of-service'),
        },
      ]
    },
    {
      title: 'Subscription',
      icon: CreditCard,
      items: [
        ...(subscriptionInfo?.is_founding_member ? [{
          label: 'Founding Member',
          description: 'Thank you for being one of our first 300 supporters! 🎉',
          info: true,
          infoIcon: Award,
          infoColor: 'text-amber-500',
        }] : []),
        {
          label: 'Current Plan',
          description: subscriptionInfo?.plan_name || (user?.subscription_status === 'tier2' ? 'Premium' : user?.subscription_status === 'tier1' ? 'Essential' : 'Free'),
          onClick: () => navigate('/pricing'),
        },
        ...(subscriptionInfo?.can_manage ? [{
          label: 'Manage Subscription',
          description: 'Update payment method, change plan, or cancel',
          onClick: handleManageSubscription,
          loading: isLoadingPortal,
          actionIcon: ExternalLink,
        }] : []),
        ...(subscriptionInfo?.pending_plan_change ? [{
          label: 'Pending Plan Change',
          description: `Changing to ${subscriptionInfo.pending_plan_change.new_plan_name} on ${subscriptionInfo.pending_plan_change.effective_date?.split('T')[0]}`,
          info: true,
          infoIcon: Clock,
          infoColor: 'text-amber-600',
        }] : []),
        {
          label: 'Reminders',
          description: 'Manage supplement and medication reminders',
          onClick: () => navigate('/reminders'),
        },
        {
          label: 'Marketing Emails',
          description: user?.email_marketing_opt_in ? 'Subscribed to updates' : 'Unsubscribed from updates',
          onClick: () => toast.info('Email preferences coming soon!'),
        },
      ]
    },
    {
      title: 'Data Management',
      icon: Download,
      items: [
        {
          label: 'Export My Data',
          description: 'Download all your data in JSON format',
          onClick: handleExportData,
          loading: isExporting,
        },
      ]
    },
  ];

  return (
    <div className="space-y-8" data-testid="settings-page">
      <Button 
        onClick={() => navigate('/dashboard')} 
        variant="ghost" 
        className="text-primary hover:text-primary/80"
        data-testid="back-button"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back to Dashboard
      </Button>

      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl p-8">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-4">
            <Settings className="w-10 h-10 text-primary" strokeWidth={1.5} />
            <h1 className="text-3xl font-nunito font-bold text-[#2D2D2D]">
              Settings
            </h1>
          </div>
          {/* Founding Member Badge */}
          {subscriptionInfo?.is_founding_member && (
            <div 
              className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-yellow-400 text-white px-4 py-2 rounded-full shadow-lg"
              data-testid="founding-member-badge"
            >
              <Award className="w-5 h-5" />
              <span className="font-semibold text-sm">Founding Member</span>
            </div>
          )}
        </div>
        <p className="text-[#57534E]">
          Manage your account, privacy, and preferences
        </p>
      </div>

      {/* Subscription Renewal Card */}
      <SubscriptionRenewalCard
        info={subscriptionInfo}
        userTier={user?.subscription_status}
        onUpgrade={() => navigate('/pricing')}
        onManage={handleManageSubscription}
        isLoadingPortal={isLoadingPortal}
      />

      {/* Settings Sections */}
      <div className="space-y-6">
        {settingsSections.map((section) => {
          const Icon = section.icon;
          return (
            <Card key={section.title} className="bg-white rounded-xl border border-stone-100 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-stone-100 bg-stone-50">
                <h2 className="text-lg font-nunito font-bold text-[#2D2D2D] flex items-center gap-3">
                  <Icon className="w-5 h-5 text-primary" />
                  {section.title}
                </h2>
              </div>
              <div className="divide-y divide-stone-100">
                {section.items.map((item, index) => (
                  <div
                    key={index}
                    className={`px-6 py-4 flex items-center justify-between ${
                      item.onClick ? 'hover:bg-stone-50 cursor-pointer transition-colors' : ''
                    }`}
                    onClick={item.onClick}
                    data-testid={`setting-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-[#2D2D2D]">{item.label}</p>
                      <p className="text-sm text-[#57534E]">{item.description}</p>
                    </div>
                    {item.info ? (
                      <item.infoIcon className={`w-5 h-5 ${item.infoColor}`} />
                    ) : item.loading ? (
                      <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-[#A8A29E]" />
                    )}
                  </div>
                ))}
              </div>
            </Card>
          );
        })}

        {/* Blocked Users Section */}
        <Card className="bg-white rounded-xl border border-stone-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-stone-100 bg-stone-50">
            <h2 className="text-lg font-nunito font-bold text-[#2D2D2D] flex items-center gap-3">
              <Ban className="w-5 h-5 text-primary" />
              Blocked Users
            </h2>
          </div>
          <div className="p-6">
            {blockedUsers.length === 0 ? (
              <p className="text-sm text-[#57534E]">
                You haven't blocked any users. When you block someone in the Community Forum, 
                their posts and replies will be hidden from you.
              </p>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-[#57534E] mb-4">
                  {blockedUsers.length} blocked user{blockedUsers.length !== 1 ? 's' : ''}. 
                  Their posts and replies are hidden from your view.
                </p>
                {blockedUsers.map((blockedUser) => (
                  <div 
                    key={blockedUser.id} 
                    className="flex items-center justify-between p-3 bg-stone-50 rounded-lg"
                    data-testid={`blocked-user-${blockedUser.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <UserX className="w-5 h-5 text-stone-400" />
                      <span className="font-medium text-[#2D2D2D]">{blockedUser.name}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUnblockUser(blockedUser.id, blockedUser.name)}
                      className="text-primary border-primary hover:bg-primary/10"
                      data-testid={`unblock-${blockedUser.id}`}
                    >
                      Unblock
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Danger Zone */}
        <Card className="bg-white rounded-xl border-2 border-red-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-red-200 bg-red-50">
            <h2 className="text-lg font-nunito font-bold text-red-700 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5" />
              Danger Zone
            </h2>
          </div>
          <div className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium text-[#2D2D2D]">Delete Account</p>
                <p className="text-sm text-[#57534E] mt-1">
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
              </div>
              <Button 
                variant="destructive" 
                onClick={() => setShowDeleteDialog(true)}
                className="bg-red-600 hover:bg-red-700"
                data-testid="delete-account-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Account
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Delete Account Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-3 text-red-700">
              <AlertTriangle className="w-6 h-6" />
              Delete Account?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                This will permanently delete your account and all your data, including:
              </p>
              <ul className="list-disc list-inside space-y-1 text-[#57534E]">
                <li>Profile information</li>
                <li>All symptom, food, and lab tracking data</li>
                <li>Journal entries and notes</li>
                <li>Forum posts and chat history</li>
                <li>Subscription (if active)</li>
              </ul>
              <p className="font-semibold text-red-600">
                This action CANNOT be undone.
              </p>
              <div className="mt-4">
                <label className="text-sm font-medium text-[#2D2D2D]">
                  Type DELETE to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="mt-2 w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="DELETE"
                  data-testid="delete-confirm-input"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={deleteConfirmText !== 'DELETE' || isDeleting}
              className="bg-red-600 hover:bg-red-700 disabled:bg-red-300"
              data-testid="confirm-delete-button"
            >
              {isDeleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete My Account'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Profile Edit Dialog */}
      <AlertDialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-3 text-primary">
              <User className="w-6 h-6" />
              Edit Profile
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <div className="mt-4">
                <label className="text-sm font-medium text-[#2D2D2D]">
                  Your Name
                </label>
                <input
                  type="text"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  className="mt-2 w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Enter your name"
                  data-testid="profile-name-input"
                />
              </div>
              <div className="mt-2">
                <label className="text-sm font-medium text-[#2D2D2D]">
                  Email
                </label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="mt-2 w-full px-3 py-2 border border-stone-200 rounded-lg bg-stone-50 text-stone-500 cursor-not-allowed"
                  data-testid="profile-email-input"
                />
                <p className="text-xs text-stone-400 mt-1">Email cannot be changed</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSavingProfile}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSaveProfile}
              disabled={!profileName.trim() || isSavingProfile}
              className="bg-primary hover:bg-primary/90 disabled:bg-primary/50"
              data-testid="save-profile-button"
            >
              {isSavingProfile ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Password Change Dialog */}
      <AlertDialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-3 text-primary">
              <Shield className="w-6 h-6" />
              Change Password
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <div className="mt-4">
                <label className="text-sm font-medium text-[#2D2D2D]">
                  Current Password
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="mt-2 w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Enter your current password"
                  data-testid="current-password-input"
                />
              </div>
              <div className="mt-2">
                <label className="text-sm font-medium text-[#2D2D2D]">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-2 w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Enter new password (min 6 characters)"
                  data-testid="new-password-input"
                />
              </div>
              <div className="mt-2">
                <label className="text-sm font-medium text-[#2D2D2D]">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-2 w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isChangingPassword}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleChangePassword}
              disabled={!currentPassword || !newPassword || !confirmPassword || isChangingPassword}
              className="bg-primary hover:bg-primary/90 disabled:bg-primary/50"
              data-testid="change-password-button"
            >
              {isChangingPassword ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Changing...
                </>
              ) : (
                'Change Password'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SettingsPage;

/**
 * SubscriptionRenewalCard
 * Displays a friendly summary of the user's current subscription, when it
 * renews / expires, and the right action button for their situation.
 */
function SubscriptionRenewalCard({ info, userTier, onUpgrade, onManage, isLoadingPortal }) {
  // While loading, show a subtle skeleton (don't block the rest of settings)
  if (!info) {
    return (
      <Card className="bg-white rounded-xl border border-stone-100 shadow-sm p-6 animate-pulse" data-testid="subscription-renewal-card-loading">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-stone-100" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-32 bg-stone-100 rounded" />
            <div className="h-3 w-48 bg-stone-100 rounded" />
          </div>
        </div>
      </Card>
    );
  }

  const renewal = info.renewal || {};
  const type = renewal.type || 'unknown';
  const daysRemaining = renewal.days_remaining;
  const planName = info.plan_name || (userTier === 'tier2' ? 'Premium' : userTier === 'tier1' ? 'Essential' : 'Free');

  // Theme picker based on subscription state
  const themes = {
    free: {
      gradient: 'from-stone-50 to-stone-100',
      border: 'border-stone-200',
      iconBg: 'bg-gradient-to-br from-stone-300 to-stone-500',
      icon: Sparkles,
      accentText: 'text-stone-700',
    },
    admin: {
      gradient: 'from-amber-50 via-yellow-50 to-orange-50',
      border: 'border-amber-200',
      iconBg: 'bg-gradient-to-br from-amber-400 to-orange-500',
      icon: Award,
      accentText: 'text-amber-700',
    },
    stripe_active: {
      gradient: 'from-emerald-50 via-teal-50 to-sky-50',
      border: 'border-emerald-200',
      iconBg: 'bg-gradient-to-br from-emerald-400 to-teal-500',
      icon: RefreshCw,
      accentText: 'text-emerald-700',
    },
    stripe_canceling: {
      gradient: 'from-amber-50 to-orange-50',
      border: 'border-amber-200',
      iconBg: 'bg-gradient-to-br from-amber-400 to-orange-500',
      icon: Clock,
      accentText: 'text-amber-700',
    },
    apple_iap: {
      gradient: 'from-sky-50 via-blue-50 to-indigo-50',
      border: 'border-sky-200',
      iconBg: 'bg-gradient-to-br from-sky-400 to-indigo-500',
      icon: RefreshCw,
      accentText: 'text-sky-700',
    },
    promo: {
      gradient: 'from-purple-50 via-fuchsia-50 to-pink-50',
      border: 'border-fuchsia-200',
      iconBg: 'bg-gradient-to-br from-fuchsia-400 to-purple-500',
      icon: Gift,
      accentText: 'text-fuchsia-700',
    },
    manual: {
      gradient: 'from-indigo-50 to-purple-50',
      border: 'border-indigo-200',
      iconBg: 'bg-gradient-to-br from-indigo-400 to-purple-500',
      icon: Calendar,
      accentText: 'text-indigo-700',
    },
  };
  const theme = themes[type] || themes.manual;
  const IconCmp = theme.icon;

  // Friendly "warning" badge when expiring soon
  const expiringSoon = typeof daysRemaining === 'number' && daysRemaining >= 0 && daysRemaining <= 14;
  const isExpired = typeof daysRemaining === 'number' && daysRemaining < 0;

  // Determine right-side action button
  let actionButton = null;
  if (type === 'free') {
    actionButton = (
      <Button
        onClick={onUpgrade}
        className="bg-primary text-white hover:bg-primary/90 whitespace-nowrap"
        data-testid="renewal-upgrade-btn"
      >
        Upgrade Plan
      </Button>
    );
  } else if (info.can_manage) {
    actionButton = (
      <Button
        onClick={onManage}
        variant="outline"
        disabled={isLoadingPortal}
        className="border-stone-300 whitespace-nowrap"
        data-testid="renewal-manage-btn"
      >
        {isLoadingPortal ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <ExternalLink className="w-4 h-4 mr-2" />
        )}
        Manage
      </Button>
    );
  } else if (type === 'promo' || type === 'manual') {
    actionButton = (
      <Button
        onClick={onUpgrade}
        className="bg-primary text-white hover:bg-primary/90 whitespace-nowrap"
        data-testid="renewal-upgrade-btn"
      >
        Choose Plan
      </Button>
    );
  } else if (type === 'apple_iap') {
    actionButton = (
      <p className="text-xs text-stone-500 max-w-[180px] text-right">
        Manage in iPhone Settings
      </p>
    );
  }

  return (
    <Card
      className={`bg-gradient-to-br ${theme.gradient} border ${theme.border} shadow-sm p-6`}
      data-testid="subscription-renewal-card"
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className={`w-12 h-12 rounded-full ${theme.iconBg} flex items-center justify-center shadow-md flex-shrink-0`}>
            <IconCmp className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-nunito font-bold text-[#2D2D2D]" data-testid="renewal-plan-name">
                {type === 'admin' ? planName : `${planName} Plan`}
              </h3>
              {info.is_founding_member && (
                <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                  <Award className="w-3 h-3" /> Founding Member
                </span>
              )}
              {expiringSoon && !isExpired && (
                <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                  Expires soon
                </span>
              )}
              {isExpired && (
                <span className="text-xs font-semibold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                  Expired
                </span>
              )}
            </div>
            <p className={`font-medium mt-1 ${theme.accentText}`} data-testid="renewal-message">
              {renewal.message || 'Active subscription'}
            </p>
            {renewal.subline && (
              <p className="text-sm text-[#57534E] mt-1" data-testid="renewal-subline">
                {renewal.subline}
              </p>
            )}
            {typeof daysRemaining === 'number' && daysRemaining >= 0 && daysRemaining <= 60 && type !== 'free' && type !== 'admin' && (
              <div className="mt-3">
                <div className="flex justify-between text-xs text-[#57534E] mb-1">
                  <span>{daysRemaining} day{daysRemaining === 1 ? '' : 's'} remaining</span>
                  {renewal.next_billing_date && (
                    <span>{formatLocalDate(renewal.next_billing_date, 'en-US', { month: 'short', day: 'numeric' })}</span>
                  )}
                  {!renewal.next_billing_date && renewal.expires_at && (
                    <span>{formatLocalDate(renewal.expires_at, 'en-US', { month: 'short', day: 'numeric' })}</span>
                  )}
                </div>
                <div className="h-1.5 bg-white/70 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      daysRemaining <= 7 ? 'bg-red-400' :
                      daysRemaining <= 14 ? 'bg-amber-400' :
                      daysRemaining <= 30 ? 'bg-yellow-400' :
                      'bg-emerald-400'
                    }`}
                    style={{ width: `${Math.max(2, Math.min(100, (daysRemaining / 60) * 100))}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        {actionButton && (
          <div className="flex-shrink-0">
            {actionButton}
          </div>
        )}
      </div>
    </Card>
  );
}
